import { scanClipboardItems, scanDataTransferItems, scanFileList } from '../core/FileEntryScanner';
import { ScannedFile } from '../core/types';
import { requireElement } from './dom';

export interface DropZoneCallbacks {
  onFilesAdded: (files: ScannedFile[]) => void;
  isEnabled: () => boolean;
}

/**
 * Wires every GUI upload entry point required: drag & drop (files, folders,
 * multiple, nested, mixed), the two picker buttons (native multi-select via
 * Ctrl/Shift is a browser default for <input multiple>, folder picking via
 * webkitdirectory), and clipboard paste (images/screenshots).
 */
export class DropZone {
  private readonly el: HTMLElement;
  private readonly fileInput: HTMLInputElement;
  private readonly folderInput: HTMLInputElement;

  constructor(private readonly callbacks: DropZoneCallbacks) {
    this.el = requireElement('dropzone');
    this.fileInput = requireElement('file-input');
    this.folderInput = requireElement('folder-input');
    const pickFilesBtn = requireElement<HTMLButtonElement>('pick-files-btn');
    const pickFolderBtn = requireElement<HTMLButtonElement>('pick-folder-btn');

    pickFilesBtn.addEventListener('click', () => this.guarded(() => this.fileInput.click()));
    pickFolderBtn.addEventListener('click', () => this.guarded(() => this.folderInput.click()));

    this.fileInput.addEventListener('change', () => {
      if (this.fileInput.files?.length) this.callbacks.onFilesAdded(scanFileList(this.fileInput.files));
      this.fileInput.value = '';
    });
    this.folderInput.addEventListener('change', () => {
      if (this.folderInput.files?.length) this.callbacks.onFilesAdded(scanFileList(this.folderInput.files));
      this.folderInput.value = '';
    });

    this.el.addEventListener('click', (event) => {
      if (!this.callbacks.isEnabled()) return;
      const target = event.target as HTMLElement;
      if (target.closest('button')) return; // buttons already have their own handlers
      this.fileInput.click();
    });

    this.el.addEventListener('dragover', (event) => {
      event.preventDefault();
      if (this.callbacks.isEnabled()) this.el.classList.add('drag-over');
    });
    this.el.addEventListener('dragleave', () => this.el.classList.remove('drag-over'));
    this.el.addEventListener('drop', (event) => {
      event.preventDefault();
      this.el.classList.remove('drag-over');
      if (!this.callbacks.isEnabled()) return;
      void this.handleDrop(event.dataTransfer);
    });

    document.addEventListener('paste', (event: ClipboardEvent) => {
      if (!this.callbacks.isEnabled()) return;
      if (!event.clipboardData?.items?.length) return;
      const scanned = scanClipboardItems(event.clipboardData.items);
      if (scanned.length > 0) {
        event.preventDefault();
        this.callbacks.onFilesAdded(scanned);
      }
    });
  }

  setEnabled(enabled: boolean): void {
    this.el.classList.toggle('disabled', !enabled);
    this.el.setAttribute('aria-disabled', String(!enabled));
  }

  private guarded(fn: () => void): void {
    if (this.callbacks.isEnabled()) fn();
  }

  private async handleDrop(dataTransfer: DataTransfer | null): Promise<void> {
    if (!dataTransfer) return;

    if (dataTransfer.items?.length) {
      const scanned = await scanDataTransferItems(dataTransfer.items);
      if (scanned.length > 0) {
        this.callbacks.onFilesAdded(scanned);
        return;
      }
    }
    if (dataTransfer.files?.length) {
      this.callbacks.onFilesAdded(scanFileList(dataTransfer.files));
    }
  }
}
